import Logo from '@tenant/assets/logo-primary.svg';
import cn from 'classnames';
import { PanelLeft, PanelRight } from 'lucide-react';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { OrgLink } from '@atoms/OrgLink';

import { SIDEBAR_TOGGLE_TEST_ID } from './constants';
import { SidebarItem } from './SidebarItem';
import { useSidebarMenu } from './useSidebarMenu';

import './sidebar.css';

export const Sidebar = () => {
  const { sidebarItems, settingsItems } = useSidebarMenu();

  /**
   * we persist the state of the collapse, we also wait for window to be defined first in order to avoid
   * a transition if the default state is not the same as the chosen state.
   * Otherwise, the animation will briefly show and it wouldn't be a good UX.
   */
  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    'sidebar_collapsed',
    false
  );

  const toggleSidebar = useCallback(
    () => setIsCollapsed((prev) => !prev),
    [setIsCollapsed]
  );

  return (
    <nav
      id="sidebar"
      aria-label="Sidebar"
      role="navigation"
      data-testid="sidebar"
      className={cn(
        'sticky top-0 z-40 flex h-screen flex-col border-r bg-white transition-all duration-300 ease-in-out',
        {
          'w-18': isCollapsed,
          'w-64': !isCollapsed
        }
      )}
    >
      <div className="flex h-full flex-1 flex-col overflow-hidden">
        <div
          className={cn(
            'flex items-center justify-between py-4',
            isCollapsed ? 'self-center' : 'px-4'
          )}
        >
          <OrgLink to="/" className="flex items-center overflow-hidden">
            {!isCollapsed && (
              <Logo className="h-7 w-auto max-w-full transition-all duration-300" />
            )}
          </OrgLink>
          <button
            type="button"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={toggleSidebar}
            className="flex size-9 items-center justify-center rounded-full hover:border-border-brand hover:text-content-brand"
            data-testid={SIDEBAR_TOGGLE_TEST_ID}
          >
            {isCollapsed ? (
              <PanelRight className="size-5" />
            ) : (
              <PanelLeft className="size-5" />
            )}
          </button>
        </div>

        <div
          className={cn(
            'sidebar-scroll flex flex-1 flex-col justify-between overflow-y-auto pb-4',
            {
              'px-2': isCollapsed,
              'px-3.5': !isCollapsed
            }
          )}
        >
          <nav className="flex flex-col gap-y-4">
            {sidebarItems.map((item) => (
              <SidebarItem
                key={item.id ?? item['data-testid']}
                data-testid={item['data-testid']}
                item={item}
                sidebarOpen={!isCollapsed}
              />
            ))}
          </nav>

          <nav className="flex flex-col gap-y-1 pt-6">
            {settingsItems.map((item) => (
              <SidebarItem
                key={item.id ?? item['data-testid']}
                data-testid={item['data-testid']}
                item={item}
                sidebarOpen={!isCollapsed}
              />
            ))}
          </nav>
        </div>
      </div>
    </nav>
  );
};
